// frontend/src/context/MetricsContext.jsx
// import clientWs from "../graphql/client-ws";
import { gql, useSubscription } from "@apollo/client";
import React, { createContext, useContext, useEffect, useState, useRef } from "react";
import { getBackendUrl } from "../config";

const backendUrl = getBackendUrl();

const MetricsContext = createContext(null);

export function MetricsProvider({ children }) {
  const [entropy_total, setEntropyTotal] = useState(0);
  const [entropy_ui_total, setEntropyUiTotal] = useState(0);
  const [entropy_dynamic_total, setEntropyDynamicTotal] = useState(0);
  const [scores, setScores] = useState({});
  const [modules, setModules] = useState({});
  const [connectionStatus, setConnectionStatus] = useState("connecting");

  const evtSourceRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const lastUpdateRef = useRef(Date.now());

  // 🔹 Initialisation SSE avec reconnexion automatique
  const initSSE = () => {
    if (evtSourceRef.current) evtSourceRef.current.close();

    console.log(`[SSE] Connexion à /api/entropy/stream...`);
    // ✅ CORRECTION ICI : Chemin relatif pour fonctionner partout (Windows/Linux)
    evtSourceRef.current = new EventSource("/api/entropy/stream");

    evtSourceRef.current.onopen = () => {
      console.log("[SSE] ✅ Connexion établie");
      setConnectionStatus("connected");
      reconnectAttemptsRef.current = 0;
    };

    evtSourceRef.current.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        lastUpdateRef.current = Date.now();
        // console.log("[SSE data]", data); // Décommente pour debug si besoin

        if (typeof data.entropy_total === "number") setEntropyTotal(data.entropy_total);
        if (typeof data.entropy_ui_total === "number") setEntropyUiTotal(data.entropy_ui_total);
        if (typeof data.entropy_dynamic_total === "number") setEntropyDynamicTotal(data.entropy_dynamic_total);
        if (data.scores && typeof data.scores === "object") setScores(data.scores);
        if (data.modules && typeof data.modules === "object") setModules(data.modules);

        setConnectionStatus("connected");
      } catch (err) {
        console.error("[SSE] Erreur parsing:", err);
      }
    };

    evtSourceRef.current.onerror = (err) => {
      console.warn("[SSE] ⚠️ Connexion perdue, reconnexion...", err);
      setConnectionStatus("reconnecting");
      if (evtSourceRef.current) evtSourceRef.current.close();

      const maxAttempts = 10;
      const baseDelay = 1000;
      
      if (reconnectAttemptsRef.current < maxAttempts) {
        const delay = baseDelay * Math.pow(2, reconnectAttemptsRef.current);
        reconnectAttemptsRef.current++;
        console.log(`[SSE] Tentative ${reconnectAttemptsRef.current}/${maxAttempts} dans ${delay}ms`);
        reconnectTimeoutRef.current = setTimeout(initSSE, delay);
      } else {
        console.error("[SSE] ❌ Échec de reconnexion après", maxAttempts, "tentatives");
        setConnectionStatus("failed");
      }
    };
  };

  // 🔹 Watchdog (reconnexion forcée si aucune donnée > 15s)
  useEffect(() => {
    const watchdog = setInterval(() => {
      if (Date.now() - lastUpdateRef.current > 15000) {
        console.warn("[SSE] 💤 Aucune donnée depuis 15s, reconnexion forcée");
        reconnect();
      }
    }, 5000);
    return () => clearInterval(watchdog);
  }, []);

  useEffect(() => {
    initSSE();
    return () => {
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
      if (evtSourceRef.current) evtSourceRef.current.close();
    };
  }, []);

  const reconnect = () => {
    reconnectAttemptsRef.current = 0;
    setConnectionStatus("connecting");
    initSSE();
  };

  // ============================================================
  // 🔹 Subscription temps réel via GraphQL-WS (entropie dynamique)
  // ============================================================
  /*
  useEffect(() => {
    const ENTROPY_SUBSCRIPTION = gql`
      subscription {
        entropyStream {
          id
          entropy
          txHash
          blockNumber
          timestamp
        }
      }
    `;

    const subscription = clientWs
      .subscribe({
        query: ENTROPY_SUBSCRIPTION,
      })
      .subscribe({
        next: ({ data }) => {
          if (!data?.entropyStream) return;
          console.log("📡 [WS] Entropie reçue :", data.entropyStream);
          const e = data.entropyStream;

          // 🔹 Mise à jour de la dynamique temps réel
          setEntropyDynamicTotal(e.entropy);
          setEntropyTotal((prev) => prev + e.entropy / 1000);
        },
        error: (err) => console.error("❌ [WS] Erreur GraphQL-WS :", err),
        complete: () => console.log("✅ [WS] Subscription fermée"),
      });

    return () => subscription.unsubscribe();
  }, []);
  */

  return (
    <MetricsContext.Provider
      value={{
        entropy_total,
        entropy_ui_total,
        entropy_dynamic_total,
        scores,
        modules,
        connectionStatus,
        reconnect,
      }}
    >
      {children}
    </MetricsContext.Provider>
  );
}

export function useMetrics() {
  const ctx = useContext(MetricsContext);
  if (!ctx) throw new Error("useMetrics must be used inside MetricsProvider");
  return ctx;
}
